<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import CatalogAPI from 'dashboard/api/catalog';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ source: 'variable', variable_key: '', products: [] }),
  },
  mode: { type: String, default: 'single' }, // 'single' | 'multi'
  min: { type: Number, default: 1 },
  max: { type: Number, default: 30 },
  availableVariables: { type: Array, default: () => [] },
  resolveLabel: { type: Function, default: label => label ?? '' },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

function update(patch) {
  emit('update:modelValue', { ...(props.modelValue ?? {}), ...patch });
}

const showVariableTab = computed(() => props.availableVariables.length > 0);

const activeTab = ref(
  !showVariableTab.value || props.modelValue?.source === 'catalog'
    ? 'catalog'
    : 'variable'
);

function setTab(tab) {
  activeTab.value = tab;
  update({ source: tab });
}

// ── Variable tab ─────────────────────────────────────────────────────────────

const variableKey = computed({
  get: () => props.modelValue?.variable_key ?? '',
  set: val => update({ source: 'variable', variable_key: val }),
});

const showVarPicker = ref(false);
const varSearch = ref('');
const varSearchInputRef = ref(null);

const filteredVars = computed(() => {
  const q = varSearch.value.toLowerCase();
  if (!q) return props.availableVariables;
  return props.availableVariables.filter(v =>
    props.resolveLabel(v.label).toLowerCase().includes(q)
  );
});

function pickVariable(key) {
  variableKey.value = key;
  showVarPicker.value = false;
  varSearch.value = '';
}

watch(showVarPicker, open => {
  if (open) nextTick(() => varSearchInputRef.value?.focus());
});

// ── Catalog tab ──────────────────────────────────────────────────────────────

const showCatalogPicker = ref(false);
const allProducts = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const searchQuery = ref('');
const searchQuerySent = ref('');
const currentPage = ref(1);
const hasMore = ref(false);
const searchDebounce = ref(null);
const catalogSearchInputRef = ref(null);

async function fetchProducts(query = '', page = 1, append = false) {
  try {
    if (page === 1) loading.value = true;
    else loadingMore.value = true;
    const response = await CatalogAPI.getProducts({ query, page });
    const newProducts = response.data.products || [];
    allProducts.value = append
      ? [...allProducts.value, ...newProducts]
      : newProducts;
    currentPage.value = response.data.page || page;
    hasMore.value = response.data.has_more || false;
  } catch {
    if (!append) allProducts.value = [];
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

function onSearchInput(event) {
  const query = event.target.value;
  searchQuery.value = query;
  clearTimeout(searchDebounce.value);
  searchDebounce.value = setTimeout(() => {
    searchQuerySent.value = query;
    fetchProducts(query, 1, false);
  }, 300);
}

function onScroll(event) {
  const el = event.target;
  if (
    el.scrollTop + el.clientHeight >= el.scrollHeight - 20 &&
    hasMore.value &&
    !loadingMore.value
  ) {
    fetchProducts(searchQuerySent.value, currentPage.value + 1, true);
  }
}

function openCatalogPicker() {
  showCatalogPicker.value = true;
  if (allProducts.value.length === 0) fetchProducts();
  nextTick(() => catalogSearchInputRef.value?.focus());
}

function closeCatalogPicker() {
  showCatalogPicker.value = false;
}

const selectedProducts = computed(() => props.modelValue?.products ?? []);

function firstSendableVariant(product) {
  return product.variants?.find(v => v.meta_retailer_id) ?? null;
}

function isSelected(product) {
  const v = firstSendableVariant(product);
  return v
    ? selectedProducts.value.some(p => p.retailer_id === v.meta_retailer_id)
    : false;
}

function toggleProduct(product) {
  const v = firstSendableVariant(product);
  if (!v) return;

  const entry = {
    retailer_id: v.meta_retailer_id,
    handle: product.handle,
    product_title: product.title,
    price: v.price,
    image_url: product.image_url,
  };

  if (props.mode === 'single') {
    update({ source: 'catalog', products: [entry] });
    closeCatalogPicker();
    return;
  }

  const current = selectedProducts.value;
  if (isSelected(product)) {
    update({
      source: 'catalog',
      products: current.filter(p => p.retailer_id !== v.meta_retailer_id),
    });
  } else if (current.length < props.max) {
    update({ source: 'catalog', products: [...current, entry] });
  }
}

function removeProduct(retailerId) {
  update({
    source: 'catalog',
    products: selectedProducts.value.filter(p => p.retailer_id !== retailerId),
  });
}

const canAddMore = computed(() =>
  props.mode === 'single'
    ? selectedProducts.value.length === 0
    : selectedProducts.value.length < props.max
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <!-- Source tabs -->
    <div v-if="showVariableTab" class="flex gap-1 p-0.5 bg-n-alpha-1 rounded-lg">
      <button
        type="button"
        class="flex-1 py-1.5 text-xs font-medium rounded-md transition-colors"
        :class="
          activeTab === 'variable'
            ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
            : 'text-n-slate-9 hover:text-n-slate-11'
        "
        @click="setTab('variable')"
      >
        {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_SOURCE_VARIABLE') }}
      </button>
      <button
        type="button"
        class="flex-1 py-1.5 text-xs font-medium rounded-md transition-colors"
        :class="
          activeTab === 'catalog'
            ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
            : 'text-n-slate-9 hover:text-n-slate-11'
        "
        @click="setTab('catalog')"
      >
        {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_SOURCE_CATALOG') }}
      </button>
    </div>

    <!-- Variable tab -->
    <div v-if="activeTab === 'variable'" class="flex flex-col gap-1.5">
      <label class="text-xs text-n-slate-9">
        {{
          mode === 'single'
            ? t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_VARIABLE_SINGLE')
            : t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_VARIABLE_MULTI')
        }}
      </label>
      <div
        v-on-click-outside="() => { showVarPicker = false; varSearch = ''; }"
        class="relative"
      >
        <div
          class="flex items-center gap-1 px-2 w-full rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus-within:outline-n-brand transition-all"
        >
          <input
            type="text"
            class="reset-base flex-1 min-w-0 h-10 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9 font-mono"
            :value="variableKey"
            :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_SELECT_VARIABLE')"
            @input="variableKey = $event.target.value"
          />
          <button
            type="button"
            class="shrink-0 inline-flex items-center justify-center size-6 rounded-md text-n-slate-11 hover:bg-n-slate-9/10 hover:text-n-brand transition-colors"
            :title="t('FLOW_BUILDER.COMMON.INSERT_VARIABLE')"
            @click.stop="showVarPicker = !showVarPicker"
          >
            <span class="i-ph-brackets-curly size-3.5 flex-shrink-0" />
          </button>
        </div>

        <div
          v-if="showVarPicker"
          class="absolute top-full right-0 mt-1 z-[200] bg-n-solid-2 border border-n-strong rounded-lg shadow-lg w-full overflow-hidden"
        >
          <div class="px-2 pt-2 pb-1">
            <div
              class="flex items-center gap-1.5 px-2 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak focus-within:outline-n-brand"
            >
              <span class="i-lucide-search size-3 text-n-slate-9 shrink-0" />
              <input
                ref="varSearchInputRef"
                v-model="varSearch"
                type="text"
                class="reset-base flex-1 min-w-0 h-7 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              />
            </div>
          </div>
          <div class="max-h-[160px] overflow-y-auto py-1 scrollbar-custom">
            <button
              v-for="v in filteredVars"
              :key="v.key"
              type="button"
              class="flex items-center w-full px-3 py-1.5 text-xs text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="pickVariable(v.key)"
            >
              {{ resolveLabel(v.label) }}
            </button>
            <p
              v-if="!filteredVars.length"
              class="px-3 py-2 text-xs text-n-slate-9 italic"
            >
              {{ t('FLOW_BUILDER.COMMON.NO_RESULTS') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Catalog tab -->
    <template v-else>
      <!-- Selected products -->
      <div v-if="selectedProducts.length" class="flex flex-col gap-1">
        <div
          v-for="p in selectedProducts"
          :key="p.retailer_id"
          class="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-n-alpha-1 border border-n-weak"
        >
          <img
            v-if="p.image_url"
            :src="p.image_url"
            class="size-7 rounded object-cover shrink-0"
          />
          <div v-else class="size-7 rounded bg-n-slate-4 shrink-0" />
          <span class="text-xs text-n-slate-12 flex-1 min-w-0 truncate">
            {{ p.product_title }}
          </span>
          <button
            type="button"
            class="i-lucide-x size-3.5 text-n-slate-9 hover:text-n-ruby-11 shrink-0"
            @click="removeProduct(p.retailer_id)"
          />
        </div>
        <p
          v-if="mode !== 'single' && min > 1 && selectedProducts.length < min"
          class="text-xs text-n-amber-11"
        >
          {{
            t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_MIN_WARNING', {
              min,
              count: selectedProducts.length,
            })
          }}
        </p>
      </div>

      <!-- Add product button + popover -->
      <div
        v-if="canAddMore"
        v-on-click-outside="closeCatalogPicker"
        class="relative"
      >
        <button
          type="button"
          class="flex items-center gap-1.5 w-full px-3 py-2 rounded-lg border border-dashed border-n-weak text-xs text-n-slate-9 hover:text-n-slate-12 hover:border-n-slate-7 hover:bg-n-alpha-1 transition-colors"
          @click="openCatalogPicker"
        >
          <span class="i-lucide-plus size-3.5 shrink-0" />
          {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_ADD') }}
        </button>

        <div
          v-if="showCatalogPicker"
          class="absolute bottom-full left-0 mb-1 z-[200] w-72 rounded-lg border border-n-weak bg-n-solid-2 shadow-lg overflow-hidden"
        >
          <div class="p-2 border-b border-n-weak">
            <input
              ref="catalogSearchInputRef"
              :value="searchQuery"
              :placeholder="t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_SEARCH')"
              class="w-full text-xs px-2 py-1.5 rounded border border-n-weak outline-none focus:outline-n-brand bg-transparent text-n-slate-12 placeholder:text-n-slate-9"
              @input="onSearchInput"
            />
          </div>

          <div
            class="overflow-y-auto max-h-52 scrollbar-custom"
            @scroll="onScroll"
          >
            <div
              v-if="loading && allProducts.length === 0"
              class="flex items-center justify-center py-6"
            >
              <span class="i-lucide-loader-circle size-4 text-n-slate-9 animate-spin" />
            </div>
            <div
              v-else-if="allProducts.length === 0"
              class="px-3 py-4 text-xs text-n-slate-9 text-center italic"
            >
              {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_EMPTY') }}
            </div>
            <template v-else>
              <button
                v-for="product in allProducts"
                :key="product.id"
                type="button"
                :disabled="!firstSendableVariant(product)"
                class="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors"
                :class="[
                  isSelected(product)
                    ? 'bg-n-brand/10'
                    : 'hover:bg-n-alpha-2',
                  !firstSendableVariant(product)
                    ? 'opacity-40 cursor-not-allowed'
                    : '',
                ]"
                @click="toggleProduct(product)"
              >
                <img
                  v-if="product.image_url"
                  :src="product.image_url"
                  :alt="product.title"
                  class="size-8 rounded object-cover shrink-0"
                />
                <div
                  v-else
                  class="size-8 rounded bg-n-solid-3 flex items-center justify-center shrink-0"
                >
                  <span class="i-lucide-image text-n-slate-10 text-xs" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-xs text-n-slate-12 truncate">{{ product.title }}</p>
                  <p
                    v-if="!firstSendableVariant(product)"
                    class="text-[10px] text-n-slate-9"
                  >
                    {{ t('FLOW_BUILDER.ACTION_FORMS.SEND_MESSAGE.PRODUCT_NO_SKU') }}
                  </p>
                </div>
                <span
                  v-if="isSelected(product)"
                  class="i-lucide-check size-3.5 text-n-brand shrink-0"
                />
              </button>
            </template>
            <div
              v-if="loadingMore"
              class="flex items-center justify-center py-2"
            >
              <span class="i-lucide-loader-circle size-4 text-n-slate-9 animate-spin" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
